import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {ProductAPIDataSource} from '../api';
import {Tag} from '../models/tag.model';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import Button from '../../../Presentation/components/ui/Button';
import Modal from '../../../Presentation/components/ui/Modal';
import TagForm from './TagForm';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

const TagList: React.FC = () => {
  const queryClient = useQueryClient();
  const {hasPermission} = useAuthorization();
  const {success: showSuccess} = useNotification();
  const {data: tags = [], isLoading, error} = useQuery({queryKey: ['tags'], queryFn: ProductAPIDataSource.getTags});

  // حالة المودال
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // إضافة وسم
  const createMutation = useMutation({
    mutationFn: (name: string) => ProductAPIDataSource.createTag({name}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['tags']});
      setModalOpen(false);
      showSuccess('تمت إضافة الوسم بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  // تعديل وسم
  const updateMutation = useMutation({
    mutationFn: ({id, name}: { id: number; name: string }) => ProductAPIDataSource.updateTag(id, {name}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['tags']});
      setModalOpen(false);
      setSelectedTag(null);
      showSuccess('تم تعديل الوسم بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  // حذف وسم
  const deleteMutation = useMutation({
    mutationFn: (id: number) => ProductAPIDataSource.deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['tags']});
      setDeleteId(null);
      showSuccess('تم حذف الوسم بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  const handleAdd = (name: string) => {
    setSubmitting(true);
    createMutation.mutate(name, {
      onSettled: () => setSubmitting(false)
    });
  };

  const handleEdit = (name: string) => {
    if (!selectedTag) {return;}
    setSubmitting(true);
    updateMutation.mutate({id: selectedTag.id, name}, {
      onSettled: () => setSubmitting(false)
    });
  };

  const handleDelete = (id: number) => {
    setDeleteLoading(true);
    deleteMutation.mutate(id, {
      onSettled: () => setDeleteLoading(false)
    });
  };

  const columns: TableColumn<Tag>[] = [
    {key: 'id', title: 'ID', render: (row) => row.id},
    {key: 'name', title: 'اسم الوسم', render: (row) => row.name},
    {key: 'slug', title: 'Slug', render: (row) => row.slug}
  ];

  // Check permissions
  const canReadTags = hasPermission(PERMISSIONS.READ_TAGS) || hasPermission(PERMISSIONS.MANAGE_PRODUCTS);
  const canCreateTags = hasPermission(PERMISSIONS.CREATE_TAGS) || hasPermission(PERMISSIONS.MANAGE_PRODUCTS);
  const canUpdateTags = hasPermission(PERMISSIONS.UPDATE_TAGS) || hasPermission(PERMISSIONS.MANAGE_PRODUCTS);
  const canDeleteTags = hasPermission(PERMISSIONS.DELETE_TAGS) || hasPermission(PERMISSIONS.MANAGE_PRODUCTS);

  if (!canReadTags) {
    return <div className="text-red-600 text-center py-8">ليس لديك صلاحية لعرض الوسوم</div>;
  }

  if (error) {
    return <div className="text-red-600 text-center py-8">حدث خطأ أثناء جلب الوسوم</div>;
  }

  return (
    <div>
      {canCreateTags && (
        <div className="flex justify-end mb-4">
          <Button onClick={() => { setModalMode('add'); setSelectedTag(null); setModalOpen(true); }}>
            إضافة وسم جديد
          </Button>
        </div>
      )}
      <AppTable
        columns={columns}
        data={tags}
        rowKey={(row, index) => `tag-${row.id}-${index || 0}`}
        emptyText={isLoading ? 'جاري التحميل...' : 'لا توجد وسوم'}
        actions={row => {
          const actions = [];

          if (canUpdateTags) {
            actions.push({
              label: 'تعديل',
              onClick: () => { setModalMode('edit'); setSelectedTag(row); setModalOpen(true); }
            });
          }

          if (canDeleteTags) {
            actions.push({
              label: 'حذف',
              onClick: () => setDeleteId(row.id),
              danger: true
            });
          }

          return actions;
        }}
        hidePagination={true}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={modalMode === 'add' ? 'إضافة وسم جديد' : 'تعديل الوسم'}>
        <TagForm
          key={modalMode === 'edit' && selectedTag ? `edit-${selectedTag.id}` : 'add'}
          initialName={modalMode === 'edit' && selectedTag ? selectedTag.name : ''}
          onSubmit={modalMode === 'add' ? handleAdd : handleEdit}
          onCancel={() => setModalOpen(false)}
          submitting={submitting}
        />
      </Modal>
      <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="تأكيد الحذف">
        <div className="text-center mb-6">
          هل أنت متأكد أنك تريد حذف هذا الوسم؟
        </div>
        <div className="flex gap-4 justify-center">
          <Button variant="ghost" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={() => handleDelete(deleteId!)} isLoading={deleteLoading}>
            حذف
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default TagList;
